import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Loader2, MessageSquare } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export function Stars({ value, onSelect = null, size = "h-4 w-4" }) {
  const [hover, setHover] = useState(0);
  const shown = onSelect ? hover || value : value;
  return (
    <div className="flex gap-0.5" onMouseLeave={onSelect ? () => setHover(0) : undefined}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n} type="button"
          onClick={onSelect ? () => onSelect(n) : undefined}
          onMouseEnter={onSelect ? () => setHover(n) : undefined}
          data-testid={onSelect ? `star-${n}` : undefined}
          aria-label={`${n} étoile(s)`}
          className={onSelect ? "cursor-pointer transition-transform hover:scale-110" : "cursor-default"}
        >
          <Star className={`${size} transition-colors ${n <= shown ? "fill-terra text-terra" : "text-line"}`} />
        </button>
      ))}
    </div>
  );
}

const inputCls = "w-full border border-line bg-cream px-4 py-3 text-sm outline-none focus:border-ink";

export default function Reviews({ productId }) {
  const [data, setData] = useState({ reviews: [], average: null, count: 0 });
  const [form, setForm] = useState({ name: "", rating: 0, comment: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get(`/products/${productId}/reviews`).then((r) => setData(r.data)).catch(() => {});
  }, [productId]);

  const submit = async () => {
    if (form.name.trim().length < 2) { toast.error("Indiquez votre nom"); return; }
    if (!form.rating) { toast.error("Choisissez une note en cliquant sur les étoiles"); return; }
    setSending(true);
    try {
      await api.post("/reviews", {
        product_id: productId,
        name: form.name.trim(),
        rating: form.rating,
        comment: form.comment.trim(),
      });
      setSent(true);
      toast.success("Merci ! Votre avis sera publié après validation par l'atelier.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mt-16 border-t border-line pt-12" data-testid="reviews-section">
      <div className="grid gap-10 md:grid-cols-2">
        {/* Liste des avis */}
        <div>
          <div className="flex items-center gap-4">
            <h2 className="font-serif text-3xl font-light text-ink">Avis clients</h2>
            {data.count > 0 && (
              <span className="flex items-center gap-2 text-sm text-ink2" data-testid="reviews-average">
                <Stars value={Math.round(data.average)} /> {data.average}/5 · {data.count} avis
              </span>
            )}
          </div>
          {data.reviews.length === 0 ? (
            <p className="mt-6 text-sm text-ink3">Aucun avis pour le moment — soyez la première personne à partager votre expérience.</p>
          ) : (
            <ul className="mt-6 space-y-5" data-testid="reviews-list">
              {data.reviews.map((r) => (
                <li key={r.id} className="border-l-2 border-terra/40 pl-4" data-testid={`review-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <Stars value={r.rating} size="h-3.5 w-3.5" />
                    <span className="text-sm font-medium text-ink">{r.name}</span>
                    <span className="text-xs text-ink3">{(r.created_at || "").slice(0, 10)}</span>
                  </div>
                  {r.comment && <p className="mt-1.5 text-sm leading-relaxed text-ink2">{r.comment}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Formulaire */}
        <div className="h-fit border border-line bg-cream p-6">
          <p className="mb-4 flex items-center gap-2 font-serif text-xl font-medium text-ink">
            <MessageSquare className="h-5 w-5 text-terra" /> Laisser un avis
          </p>
          {sent ? (
            <p className="text-sm leading-relaxed text-ink2" data-testid="review-thanks">
              Merci pour votre retour ! Il sera visible dès sa validation par l'atelier.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-widest text-ink3">Votre note</span>
                <Stars value={form.rating} onSelect={(n) => setForm({ ...form, rating: n })} size="h-5 w-5" />
              </div>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Votre nom (ex. Claire M.)" maxLength={60} data-testid="review-name" className={inputCls} />
              <textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
                placeholder="Votre expérience avec cette pièce (optionnel)" maxLength={600} rows={3}
                data-testid="review-comment" className={`${inputCls} resize-none`} />
              <button onClick={submit} disabled={sending} data-testid="review-submit"
                className="w-full bg-ink py-3 text-xs uppercase tracking-widest text-cream transition-colors hover:bg-terra disabled:opacity-50">
                {sending ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Envoyer mon avis"}
              </button>
              <p className="text-xs text-ink3">Publié après validation par l'atelier — aucun avis n'est affiché sans relecture.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
